/**
 * WaterField.ts — CPU-side proxy of the rendered water surface.
 *
 * Used by Boat buoyancy: we cannot read back the FFT cascade textures cheaply
 * on the CPU each frame, so we maintain a small Gerstner mix here whose total
 * amplitude scales with wind speed in the same way the GPU surface does.
 *
 * Visual coherence is approximate (the boat may sit a few cm above/below the
 * rendered surface) but pitch/roll motion is plausible for the sea state.
 */

const COMPS = [
  // [ angularOffset (rad from wind), period (s), baseAmp (m at U10=6) ]
  { off: -0.42, period: 2.4, baseAmp: 0.04, phase: 0.7 },
  { off: 0.31, period: 3.6, baseAmp: 0.05, phase: 1.7 },
  { off: -0.18, period: 5.1, baseAmp: 0.07, phase: 2.5 },
  { off: 0.22, period: 7.3, baseAmp: 0.09, phase: 0.3 },
] as const;

const G = 9.81;

export class WaterField {
  /** Wind direction (radians, meteo: 0 = from north). */
  windDirRad = 0;
  /** Wind speed at 10 m (m/s). */
  windSpeed = 0;
  /** Time accumulator (s). */
  time = 0;

  /** Approximate water surface elevation at world (x, z). */
  sampleHeight(x: number, z: number): number {
    let h = 0;
    const ampScale = Math.max(0.1, this.windSpeed / 6.0);
    for (const c of COMPS) {
      const dir = this.windDirRad + c.off;
      // Wind-meteo convention: the wind blows TO direction (-sin θ, +cos θ),
      // so wave propagation direction is the same.
      const dx = -Math.sin(dir);
      const dz = Math.cos(dir);
      const omega = (2 * Math.PI) / c.period;
      // Deep-water dispersion: ω² = g·k → k = ω²/g.
      const k = (omega * omega) / G;
      const phase = k * (dx * x + dz * z) - omega * this.time + c.phase;
      h += c.baseAmp * ampScale * Math.sin(phase);
    }
    return h;
  }
}
